import { useState } from 'react';
import SEO from '../../components/base/SEO';

export default function CertificatesPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Certificates & Quality Assurance - Proform Chimney Systems',
    description: 'Quality certifications and industry recognition. ISO 9001:2015, ISO 10002:2018, ISO 45001:2018 certified chimney manufacturer in Turkey.',
    url: `${siteUrl}/certificates`,
    publisher: {
      '@type': 'Organization',
      name: 'Proform Chimney Systems'
    }
  };

  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const certificates = [
    {
      id: 1,
      thumbnail: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/eebe9669125a79649281ff959215dfc2.jpeg',
      fullImage: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/eebe9669125a79649281ff959215dfc2.jpeg',
      title: 'ISO 9001:2015 Quality Management System Certificate',
      badge: 'ISO 9001'
    },
    {
      id: 2,
      thumbnail: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/255aa95b157a41ff80987a9d3514f0ca.jpeg',
      fullImage: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/255aa95b157a41ff80987a9d3514f0ca.jpeg',
      title: 'Factory Production Control Conformity Certificate',
      badge: 'FPC'
    },
    {
      id: 3,
      thumbnail: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/c84d0fde370126564d49e2ea54074282.jpeg',
      fullImage: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/c84d0fde370126564d49e2ea54074282.jpeg',
      title: 'Conformity Certificate - Chimney System Products',
      badge: 'CE'
    },
    {
      id: 4,
      thumbnail: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/608138686b6f41bb5d8794614bf08bd8.png',
      fullImage: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/608138686b6f41bb5d8794614bf08bd8.png',
      title: 'ISO 10002:2018 Certificate',
      badge: 'ISO 10002'
    },
    {
      id: 5,
      thumbnail: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/43e1d76c3c9310e02190fd25d56c83fe.png',
      fullImage: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/43e1d76c3c9310e02190fd25d56c83fe.png',
      title: 'Factory Production Control Certificate',
      badge: 'FPC'
    },
    {
      id: 6,
      thumbnail: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/1cfa5b4e8315fdf1ac89df1681dd2b2f.png',
      fullImage: 'https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/1cfa5b4e8315fdf1ac89df1681dd2b2f.png',
      title: 'ISO 45001:2018 Certificate',
      badge: 'ISO 45001'
    }
  ];

  return (
    <>
      <SEO
        title="Quality Certificates - ISO Certified Chimney Manufacturer | Proform Turkey"
        description="Proform Chimney Systems quality certifications: ISO 9001:2015, ISO 10002:2018, ISO 45001:2018. Certified excellence in chimney manufacturing and safety standards in Turkey."
        keywords="ISO certified chimney, quality certificates Turkey, chimney safety standards, ISO 9001 chimney, certified manufacturer Istanbul"
        canonical={`${siteUrl}/certificates`}
        schema={schema}
      />
      <div className="min-h-screen bg-white pt-20">
        {/* Hero Section */}
        <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://public.readdy.ai/ai/img_res/e64b4cce38206c00b4d17198a70fec61.jpg"
              alt="Certificates Hero"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          </div>
          
          <div className="relative z-10 text-center text-white px-6">
            <div className="inline-block bg-orange-500/20 backdrop-blur-sm border border-orange-500/30 px-6 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-award-fill mr-2"></i>
              Quality Assurance
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">Our Certificates</h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto font-light">
              Quality and safety at international standards
            </p>
          </div>
        </section>

        {/* ISO Badges Section */}
        <section className="py-20 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className="ri-shield-check-fill text-orange-500 text-4xl"></i>
                </div>
                <div className="text-white font-bold text-xl">ISO 9001:2015</div>
                <div className="text-white/80 text-sm mt-2">Quality Management</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className="ri-customer-service-2-fill text-orange-500 text-4xl"></i>
                </div>
                <div className="text-white font-bold text-xl">ISO 10002:2018</div>
                <div className="text-white/80 text-sm mt-2">Customer Satisfaction</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-center hover:bg-white/20 transition-all duration-300 col-span-2 md:col-span-1">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className="ri-shield-user-fill text-orange-500 text-4xl"></i>
                </div>
                <div className="text-white font-bold text-xl">ISO 45001:2018</div>
                <div className="text-white/80 text-sm mt-2">Occupational Health &amp; Safety</div>
              </div>
            </div>
          </div>
        </section>

        {/* Certificates Content */}
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Quality Assurance &amp; Certifications
              </h2>
              <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
                At Proform Chimney Systems, we maintain the highest quality and safety standards. Our certificates demonstrate our commitment to excellence and compliance with international standards.
              </p>
            </div>

            {/* Certificate Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
              {certificates.map((cert) => (
                <div
                  key={cert.id}
                  onClick={() => setSelectedImage(cert.fullImage)}
                  className="group relative bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
                >
                  {/* Badge */}
                  <div className="absolute top-4 right-4 z-10 bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    {cert.badge}
                  </div>
                  
                  {/* Image */}
                  <div className="relative overflow-hidden bg-gray-50 h-80">
                    <img
                      src={cert.thumbnail}
                      alt={cert.title}
                      className="w-full h-full object-contain p-6 group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                      <div className="bg-white/90 backdrop-blur-sm px-6 py-3 rounded-full">
                        <i className="ri-zoom-in-line text-orange-500 text-2xl mr-2"></i>
                        <span className="text-gray-900 font-semibold">Enlarge</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Title */}
                  <div className="p-6">
                    <h4 className="text-base font-bold text-gray-900 leading-snug">{cert.title}</h4>
                  </div>
                </div>
              ))}
            </div>

            {/* Standards & Compliance */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-orange-50 to-white p-10 rounded-3xl shadow-lg text-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-shield-check-fill text-white text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Quality Standards</h3>
                <p className="text-gray-700 leading-relaxed">
                  All our products meet international quality and safety standards for chimney systems.
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-white p-10 rounded-3xl shadow-lg text-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-verified-badge-fill text-white text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Industry Compliance</h3>
                <p className="text-gray-700 leading-relaxed">
                  We are fully compliant with European and Turkish industrial regulations and standards.
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-white p-10 rounded-3xl shadow-lg text-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-medal-fill text-white text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Excellence Awards</h3>
                <p className="text-gray-700 leading-relaxed">
                  Recognized for outstanding quality and innovation in chimney manufacturing.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Exhibition Logos */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Exhibitions &amp; Events</h2>
              <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
              <p className="text-gray-700 text-lg">We participate in major industry fairs worldwide</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl mx-auto">
              <div className="bg-white rounded-3xl shadow-xl p-12 flex items-center justify-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <img
                  src="https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/83549f9c862859ca7232b94120293fce.jpeg"
                  alt="Exhibition 1"
                  className="w-full h-auto object-contain max-h-48"
                />
              </div>
              <div className="bg-white rounded-3xl shadow-xl p-12 flex items-center justify-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <img
                  src="https://static.readdy.ai/image/260cb4a7e92398667a2a2f5c835f3622/438004ec34177dcdd6f3334854c87588.jpeg"
                  alt="MCE 2024"
                  className="w-full h-auto object-contain max-h-48"
                />
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-r from-orange-500 via-orange-600 to-orange-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Learn More About Our Quality Standards
            </h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              Contact us for detailed information about our certificates and quality processes
            </p>
            <a
              href="/contact"
              className="inline-block bg-white text-orange-600 px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              Contact Us
            </a>
          </div>
        </section>

        {/* Lightbox Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={() => setSelectedImage(null)}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all duration-300 cursor-pointer group"
            >
              <i className="ri-close-line text-3xl group-hover:rotate-90 transition-transform duration-300"></i>
            </button>
            <div className="relative max-w-6xl w-full">
              <img
                src={selectedImage}
                alt="Certificate"
                className="max-w-full max-h-[90vh] object-contain mx-auto rounded-2xl shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}